import { ArrowCircleUpIcon, TrashIcon } from '@phosphor-icons/react';
import React, { useEffect } from 'react';
import { useFormikContext } from 'formik';

import { Button } from '../../../../components/button/Button';
import { ProductTypeComboboxField } from '../../productType/ProductTypeComboboxField';
import { InputField } from '../../../../components/input/InputField';
import { VatRateComboboxField } from '../../vatRate/VatRateComboboxField';
import { IOrderLineSaleValues } from '../orderLineSale/CreateOrderLineSale';
import { useMinimalGeneralSettings } from '../../../../contexts/minimal-settings-context';
import { decimateAndFormatNumber, parseNumberInput } from '../../../../utils/number';
import { ICreateOrderLineFieldsProps } from './CreateOrderLineFields';
import {
  calculateDieselSurchargeFromLine,
  findDieselSurchargeBaseLine,
  getEffectiveDieselSurchargePercentage,
  isDieselSurchargeProductType,
} from '../../../../domain/diesel-surcharge';

export interface ICreateOrderLineSaleProps {
  index: number;
  onRemove: (index: number) => void;
  sales: Array<IOrderLineSaleValues>;
  customer: ICreateOrderLineFieldsProps['customer'];
}

export const CreateOrderLineSale: React.FC<ICreateOrderLineSaleProps> = (props) => {
  const { index, onRemove, sales, customer } = props;
  const formikbag = useFormikContext();
  const { settings } = useMinimalGeneralSettings();

  const dieselSurchargePercentage = getEffectiveDieselSurchargePercentage(customer, settings.dieselSurchargePercentage);

  const productType = sales[index].productType;
  const isDieselSurchargeSelected = isDieselSurchargeProductType(
    productType?.key,
    settings.dieselSurchargeProductType?.id,
  );
  useEffect(() => {
    if (isDieselSurchargeSelected) {
      const baseProduct = findDieselSurchargeBaseLine(
        sales
          .map((sale) => ({
            productTypeId: sale.productType?.key ?? '',
            amount: sale.amount ? parseNumberInput(sale.amount, 2) : 0,
            unitPrice: sale.unitPrice ? parseNumberInput(sale.unitPrice, 2) : 0,
          }))
          .filter((sale) => sale.productTypeId),
        {
          baseProductTypeId: settings.dieselSurchargeBaseProductType?.id,
          defaultProductTypeId: settings.defaultProductType?.id,
          dieselProductTypeId: settings.dieselSurchargeProductType?.id,
        },
      );
      if (baseProduct) {
        const surchargeValue = calculateDieselSurchargeFromLine(baseProduct, dieselSurchargePercentage);
        formikbag.setFieldValue(`sales[${index}].unitPrice`, decimateAndFormatNumber(surchargeValue, 2));
      }
    }
  }, [productType?.key]);

  return (
    <div className="card p-4">
      <div className="flex justify-between items-center mb-4">
        <h3 className="flex gap-2 items-center">
          <ArrowCircleUpIcon className="w-6 h-6 text-positive-text" />
          <div className="heading-three">{`Verkoop ${index + 1}`}</div>
        </h3>
        <Button
          color="danger"
          onTrigger={() => {
            onRemove(index);
          }}
          iconLeft={<TrashIcon className="button-icon" />}
        >
          Verwijder
        </Button>
      </div>

      <ProductTypeComboboxField labelText="Product" name={`sales[${index}].productType`} />

      <div className="w-full flex gap-2">
        <div className="flex-2">
          <InputField labelText="Aantal" type="number" name={`sales[${index}].amount`} step="0.01" />
        </div>
        <div className="flex-2">
          <InputField labelText="Eenheidsprijs" type="number" name={`sales[${index}].unitPrice`} step="0.01" />
        </div>
        <div className="flex-3">
          <VatRateComboboxField labelText="Btw Tarief" name={`sales[${index}].vatRate`} />
        </div>
      </div>
      <InputField labelText="Extra info" type="text" name={`sales[${index}].externalNote`} />

      {isDieselSurchargeSelected && (
        <div className="text-sm">{`Diesel toeslag voor klant is ${decimateAndFormatNumber(dieselSurchargePercentage, 2)}%`}</div>
      )}
    </div>
  );
};
